import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS, Languages } from '@avanta/shared/constants/common';
import { type Patient } from '@avanta/shared/types/patients';
import { type User } from '@avanta/shared/types/users';
import { cn } from '@avanta/shared/utils/cn';
import { dateFromBirthParts, getBirthDateParts } from '@avanta/shared/utils/date';
import { Pencil, Save, UserCog, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFetchPatientHealthCenter } from '../../hooks/useFetchPatientHealthCenter';
import {
  insuredPersonProfileForm,
  type InsuredPersonProfileForm as InsuredPersonProfileFormType,
} from './model';
import { useUpdateInsuredPerson } from './useUpdateInsuredPerson';

const EMPTY_VALUE = '—';

interface InsuredPersonProfileFormProps {
  insuredPerson: User;
}

export const InsuredPersonProfileForm = ({
  insuredPerson,
}: InsuredPersonProfileFormProps): React.ReactElement => {
  const { mutate: updateInsuredPerson, isPending } = useUpdateInsuredPerson(insuredPerson.id);
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const [isEditing, setIsEditing] = useState(false);
  const { data: healthCenterName, isLoading: isLoadingHealthCenter } = useFetchPatientHealthCenter(
    insuredPerson.departmentId
  );
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<InsuredPersonProfileFormType>({
    mode: 'onChange',
    defaultValues: {
      firstName: insuredPerson.firstName ?? '',
      surname: insuredPerson.surname ?? '',
      email: insuredPerson.email ?? '',
      nif: insuredPerson.nif ?? '',
      contactPhone: insuredPerson.contactPhone ?? '',
      gender: insuredPerson.gender?.toString() ?? '',
      dateOfBirth: dateFromBirthParts(
        insuredPerson.monthOfBirth,
        insuredPerson.yearOfBirth,
        insuredPerson.dayOfBirth
      ),
    },
  });

  const handleOnCancel = (): void => {
    reset();
    setIsEditing(false);
  };

  const handleOnSubmit = async (data: InsuredPersonProfileFormType): Promise<void> => {
    const { dateOfBirth, ...formValues } = data;
    const patient = {
      ...insuredPerson,
      ...formValues,
      gender: Number(data.gender),
      spokenLanguages: [Languages.Spanish],
      ...(dateOfBirth && getBirthDateParts(dateOfBirth)),
    } as Patient;
    updateInsuredPerson(patient, {
      onSuccess: () => {
        reset(data);
        setIsEditing(false);
      },
    });
  };

  const healthCenterDisplay = isLoadingHealthCenter
    ? t('Common.loading', { ns: LOCIZE_NS.COMMON })
    : (healthCenterName ?? EMPTY_VALUE);

  return (
    <Card
      variant="primary"
      title={t('InsuredPersonsProfile.info.heading')}
      icon={UserCog}
      className="p-0 am-md:p-0"
    >
      <form className="flex flex-col" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid grid-cols-1 gap-6 px-8 pt-8 pb-6 am-lg:grid-cols-3">
          {Object.entries(insuredPersonProfileForm).map(([id, input]) => {
            const { component: Component } = input;
            const formId = id as keyof typeof insuredPersonProfileForm;
            const formErrors = errors[formId];
            const isControllerComponent = Component !== Input;
            const rules = {
              required: input.required,
              ...(input.pattern && { pattern: input.pattern }),
              ...(input.validate && { validate: input.validate }),
              ...(input.minLength && { minLength: input.minLength }),
              ...(input.maxLength && { maxLength: input.maxLength }),
            };
            const helperText = formErrors?.message ? t(formErrors.message) : undefined;
            const fieldClassName = cn(
              'w-full',
              !isEditing && 'pointer-events-none',
              !isEditing && Component === SelectController && '[&>button_svg]:hidden'
            );

            return (
              <div key={id}>
                {isControllerComponent ? (
                  <Component
                    control={control}
                    name={formId}
                    className={fieldClassName}
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    options={input.options ?? []}
                    rules={rules}
                    variant={formErrors ? 'invalid' : 'filled'}
                    helperText={helperText}
                    leftIcon={input.leftIcon}
                  />
                ) : (
                  <Input
                    type={input.type}
                    className="w-full"
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    readOnly={!isEditing}
                    variant={formErrors ? 'invalid' : 'filled'}
                    helperText={helperText}
                    {...register(formId, rules)}
                  />
                )}
              </div>
            );
          })}
          <Input
            type="text"
            className="w-full"
            label={t('InsuredPersonsProfile.healthCenter.label')}
            value={healthCenterDisplay}
            readOnly
            variant="filled"
          />
        </div>
        <div className="flex justify-end pr-4 pb-4">
          {isEditing ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive_terciary"
                size="lg"
                icon="leading"
                onClick={handleOnCancel}
              >
                <X />
                {t('InsuredPersonsProfile.cancel.cta')}
              </Button>
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                icon="leading"
                disabled={!isDirty || !isValid}
                isLoading={isPending}
              >
                <Save />
                {t('InsuredPersonsProfile.save.cta')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="text-teal-600 hover:text-teal-600 hover:bg-transparent"
              icon="leading"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="size-4" />
              {t('InsuredPersonsProfile.edit.cta')}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};
